import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole, AuditLog } from '../types/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, role?: UserRole) => Promise<void>;
  logout: () => void;
  switchRole: (role: UserRole) => void;
  auditLogs: AuditLog[];
  logAction: (action: string, details: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SEED_USERS: Record<UserRole, User> = {
  admin: {
    id: 'user_admin_01',
    email: 'admin@schemepay.com',
    name: 'Devansh Sharma',
    role: 'admin',
    department: 'Corporate Accounts & Audit',
    isActive: true,
  },
  finance_user: {
    id: 'user_finance_01',
    email: 'finance@schemepay.com',
    name: 'Priya Patel',
    role: 'finance_user',
    department: 'Accounts Reconciliation Desk',
    isActive: true,
  },
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('schemepay_user');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return SEED_USERS.finance_user;
      }
    }
    // Default to finance user for instant friction-free access
    return SEED_USERS.finance_user;
  });

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>(() => {
    const saved = localStorage.getItem('schemepay_audit_logs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    return [
      {
        id: 'audit_init_1',
        userId: 'user_admin_01',
        userEmail: 'admin@schemepay.com',
        userName: 'Devansh Sharma',
        action: 'SYSTEM_INITIALIZATION',
        details: 'SchemePay Reconciliation Dashboard initialised with Cloudflare D1 and R2 bindings.',
        createdAt: '2026-09-01T09:00:00Z',
      },
      {
        id: 'audit_init_2',
        userId: 'user_finance_01',
        userEmail: 'finance@schemepay.com',
        userName: 'Priya Patel',
        action: 'LOGIN_SUCCESS',
        details: 'User Priya Patel authenticated to SchemePay Finance portal.',
        createdAt: '2026-09-01T09:15:20Z',
      },
    ];
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem('schemepay_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('schemepay_user');
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem('schemepay_audit_logs', JSON.stringify(auditLogs));
  }, [auditLogs]);

  const login = async (email: string, requestedRole?: UserRole) => {
    const lower = email.toLowerCase();
    const role: UserRole = requestedRole || (lower.includes('admin') ? 'admin' : 'finance_user');
    const selectedUser = SEED_USERS[role];
    setUser(selectedUser);

    await logAction('LOGIN_SUCCESS', `User ${selectedUser.name} (${selectedUser.role}) logged in.`);
  };

  const logout = () => {
    if (user) {
      logAction('LOGOUT', `User ${user.name} logged out.`);
    }
    setUser(null);
  };

  const switchRole = (role: UserRole) => {
    const selectedUser = SEED_USERS[role];
    setUser(selectedUser);
    logAction('ROLE_SWITCH', `Switched active session to ${selectedUser.name} (${role}).`);
  };

  const logAction = async (action: string, details: string) => {
    const newLog: AuditLog = {
      id: 'audit_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
      userId: user?.id || 'guest',
      userEmail: user?.email || 'finance@schemepay.com',
      userName: user?.name || 'Finance User',
      action,
      details,
      createdAt: new Date().toISOString(),
    };

    setAuditLogs((prev) => [newLog, ...prev.slice(0, 99)]);

    // Proactively post to Cloudflare Pages Functions API if online
    try {
      await fetch('/api/audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLog),
      });
    } catch {
      // Offline / client mode graceful fallback
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        login,
        logout,
        switchRole,
        auditLogs,
        logAction,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
